import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getFirestore, initializeFirestore, enableIndexedDbPersistence, connectFirestoreEmulator } from "firebase/firestore"; // For database
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from "firebase/auth"; // For authentication
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";



const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Export services
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Force the account selection prompt to appear every time a user logs in
googleProvider.setCustomParameters({ prompt: 'select_account' });

// E2E-only emulator mode. BOTH conditions must hold (deploy-safety invariant,
// see CLAUDE.md ## Deploy safety):
//   (a) explicit build-time opt-in — REACT_APP_USE_EMULATORS is set exclusively
//       by e2e/mobile-qa/e2e.env, never by .env* or any deploy workflow;
//   (b) the page is actually served from a loopback host.
// (b) exists because (a) leaked once: the harness build overwrote build/ in CI
// and dev hosting served an emulator-wired bundle (v3.36.2 incident). With the
// hostname check, a bundle on *.literacyassessments.com / *.web.app is
// structurally unable to enter emulator mode even if the flag leaks again.
export const isEmulatorMode =
  process.env.REACT_APP_USE_EMULATORS === 'true' &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

// Firebase App Check (v3.60.0 — Clever SSO abuse-guard groundwork; owner
// decision 2026-08-20: per-IP throttle + App Check, both layers). Initialized
// ONLY when the build carries REACT_APP_APPCHECK_SITE_KEY (a reCAPTCHA v3
// site key registered under Firebase App Check — an owner console step that
// has not happened yet). No workflow or .env sets the key today, so every
// current build compiles this to a no-op. Meanwhile cleverSsoExchange
// OBSERVES token presence in log-only mode; enforcement (enforceAppCheck on
// the callable) flips only after the key ships and the logs run clean.
if (process.env.REACT_APP_APPCHECK_SITE_KEY && !isEmulatorMode) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_APPCHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// WebKit (the harness's iPhone engine) aborts Firestore's streaming WebChannel
// backchannel against the local emulator, so the harness build forces long
// polling. Real builds need the same override on Apple WebKit (Safari and
// every iOS browser): a single permission-denied write permanently stalls the
// default backchannel — the Listen channel goes silent and every later read
// hangs until the 15s LoadErrorNotice (proven 2026-07-26 with a minimal repro
// against the demo project, SDK 12.13 and 12.16 both affected; forcing long
// polling cures it). The demo's by-design write lockdown denies the
// login-path presence write, which bricked every Safari session there.
// Chromium recovers from the same sequence; only Apple WebKit needs this.
const isAppleWebKit =
  typeof navigator !== 'undefined' && /apple/i.test(navigator.vendor || '');
export const db = (isEmulatorMode || isAppleWebKit)
  ? initializeFirestore(app, { experimentalForceLongPolling: true })
  : getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

// The emulator endpoints deliberately live in e2e.env, not here: CRA/terser
// does NOT reliably dead-code-eliminate this block from real bundles, so a
// hardcoded host string would ship in every deploy and the CI bundle scan
// (scripts/bundleGuard.sh) could not treat "127.0.0.1:9099" as proof of a
// leaked harness build. Real builds compile these to undefined.
if (isEmulatorMode) {
  connectAuthEmulator(auth, process.env.REACT_APP_EMULATOR_AUTH_URL, { disableWarnings: true });
  // Firestore goes through the harness static server (same origin), which
  // proxies WebChannel paths to the emulator — WebKit drops the cross-origin
  // streams otherwise. Auth/functions plain-POST endpoints are fine direct.
  connectFirestoreEmulator(db, window.location.hostname, Number(window.location.port) || 80);
  connectFunctionsEmulator(
    functions,
    process.env.REACT_APP_EMULATOR_FUNCTIONS_HOST,
    Number(process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT)
  );
}

// Offline persistence — Chromium only (v3.69.3). On Apple WebKit the
// Firestore SDK queues EVERY operation behind persistence startup, and
// Safari's IndexedDB open() can hang after wake/system pressure (a
// long-standing WebKit defect). When it does, the whole SDK queue wedges
// before the first read even starts — proven on prod 2026-08-31: Safari 27,
// long polling already forced, fresh boot after the idle sign-out, and the
// boot pipeline's very first read ("license check (email)") timed out at
// 15s while Chrome sessions on the same day were unaffected. A wedged
// IndexedDB also survives soft reloads, which is why "hard refresh
// sometimes fixed it, not always". Third WebKit special-case in this file's
// history (backchannel stall → long polling; demo presence write) — same
// theme: WebKit + Firestore extras = fragility, so WebKit runs cache-less
// and online-first while Chromium keeps the free cached reads.
if (!isAppleWebKit) {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn("Multiple tabs open, persistence can only be enabled in one tab at a a time.");
    } else if (err.code === 'unimplemented') {
      console.warn("The current browser does not support all of the features required to enable persistence");
    } else {
      console.error("Firebase persistence error:", err);
    }
  });
}


